import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const { 
  FiTrendingUp, FiCalendar, FiMapPin, FiUser, FiClock, FiCheckCircle,
  FiPlayCircle, FiFileText, FiDownload, FiMessageSquare, FiPhone,
  FiAlertCircle, FiVideo, FiCamera, FiActivity, FiFilter, FiSearch
} = FiIcons;

const PatientJourney = () => {
  const { user } = useAuth();
  const [activeJourney, setActiveJourney] = useState('current');
  const [searchTerm, setSearchTerm] = useState('');

  const patientJourneys = [
    {
      id: 1,
      patientName: 'Sarah Johnson',
      patientId: 'P-2847',
      status: 'in_progress',
      startDate: '2024-01-08',
      estimatedCompletion: '2024-06-15',
      progress: 35,
      primaryProvider: 'Dr. Michael Chen',
      specialist: 'Dr. Sarah Mitchell',
      treatmentType: 'Dental Implant Rehabilitation',
      totalSteps: 8,
      completedSteps: 3,
      currentStep: 4,
      steps: [
        {
          id: 1,
          title: 'Initial Consultation',
          status: 'completed',
          completedDate: '2024-01-08',
          provider: 'Dr. Michael Chen',
          duration: '60 min',
          notes: 'Comprehensive examination completed. Patient is a good candidate for implants.',
          documents: ['Initial_Exam_Report.pdf', 'Treatment_Options.pdf']
        },
        {
          id: 2,
          title: 'Diagnostic Imaging',
          status: 'completed',
          completedDate: '2024-01-10',
          provider: 'Dr. Michael Chen',
          duration: '45 min',
          notes: 'CBCT scans taken. Shows adequate bone density for implant placement.',
          documents: ['CBCT_Scan.dicom', 'Radiology_Report.pdf']
        },
        {
          id: 3,
          title: 'Treatment Planning',
          status: 'completed',
          completedDate: '2024-01-12',
          provider: 'Dr. Sarah Mitchell',
          duration: '90 min',
          notes: 'Comprehensive treatment plan developed. Bone grafting required.',
          documents: ['Treatment_Plan_v2.pdf', 'Cost_Estimate.pdf']
        },
        {
          id: 4,
          title: 'Bone Grafting Procedure',
          status: 'current',
          scheduledDate: '2024-01-20',
          provider: 'Dr. Sarah Mitchell',
          duration: '120 min',
          notes: 'Bilateral sinus lift and ridge augmentation scheduled.',
          documents: ['Consent_Form.pdf', 'Pre_Op_Instructions.pdf']
        },
        {
          id: 5,
          title: 'Healing Period',
          status: 'upcoming',
          scheduledDate: '2024-02-20',
          provider: 'Dr. Sarah Mitchell',
          duration: '4-6 weeks',
          notes: 'Monitoring bone integration and healing progress.',
          documents: []
        },
        {
          id: 6,
          title: 'Implant Placement',
          status: 'upcoming',
          scheduledDate: '2024-03-15',
          provider: 'Dr. Sarah Mitchell',
          duration: '180 min',
          notes: 'Placement of 4 dental implants in maxillary arch.',
          documents: []
        },
        {
          id: 7,
          title: 'Osseointegration Period',
          status: 'upcoming',
          scheduledDate: '2024-03-15',
          provider: 'Dr. Sarah Mitchell',
          duration: '3-4 months',
          notes: 'Allow time for implants to integrate with bone.',
          documents: []
        },
        {
          id: 8,
          title: 'Final Restoration',
          status: 'upcoming',
          scheduledDate: '2024-06-15',
          provider: 'Dr. Michael Chen',
          duration: '120 min',
          notes: 'Placement of final implant-supported prosthesis.',
          documents: []
        }
      ],
      milestones: [
        { title: 'Treatment Start', date: '2024-01-08', completed: true },
        { title: 'Surgery Phase', date: '2024-01-20', completed: false },
        { title: 'Restoration Phase', date: '2024-06-15', completed: false },
        { title: 'Treatment Complete', date: '2024-06-15', completed: false }
      ]
    },
    {
      id: 2,
      patientName: 'Robert Davis',
      patientId: 'P-2846',
      status: 'completed',
      startDate: '2023-09-15',
      completedDate: '2024-01-10',
      progress: 100,
      primaryProvider: 'Dr. James Wilson',
      specialist: 'Dr. Lisa Rodriguez',
      treatmentType: 'Orthodontic Treatment',
      totalSteps: 6,
      completedSteps: 6,
      currentStep: 6,
      steps: [
        {
          id: 1,
          title: 'Initial Assessment',
          status: 'completed',
          completedDate: '2023-09-15',
          provider: 'Dr. Lisa Rodriguez',
          duration: '60 min',
          notes: 'Comprehensive orthodontic evaluation completed.',
          documents: ['Orthodontic_Assessment.pdf']
        },
        {
          id: 2,
          title: 'Treatment Planning',
          status: 'completed',
          completedDate: '2023-09-22',
          provider: 'Dr. Lisa Rodriguez',
          duration: '45 min',
          notes: 'Clear aligner treatment plan established.',
          documents: ['Treatment_Plan.pdf']
        },
        {
          id: 3,
          title: 'Aligner Fitting',
          status: 'completed',
          completedDate: '2023-09-29',
          provider: 'Dr. Lisa Rodriguez',
          duration: '30 min',
          notes: 'First set of aligners fitted. Patient instructions provided.',
          documents: ['Aligner_Instructions.pdf']
        },
        {
          id: 4,
          title: 'Progress Monitoring',
          status: 'completed',
          completedDate: '2023-12-15',
          provider: 'Dr. Lisa Rodriguez',
          duration: '30 min',
          notes: 'Treatment progressing as expected. Minor adjustments made.',
          documents: ['Progress_Report.pdf']
        },
        {
          id: 5,
          title: 'Refinement Phase',
          status: 'completed',
          completedDate: '2024-01-05',
          provider: 'Dr. Lisa Rodriguez',
          duration: '45 min',
          notes: 'Final refinements completed.',
          documents: ['Refinement_Plan.pdf']
        },
        {
          id: 6,
          title: 'Retention Planning',
          status: 'completed',
          completedDate: '2024-01-10',
          provider: 'Dr. Lisa Rodriguez',
          duration: '30 min',
          notes: 'Retention appliances fitted. Treatment complete.',
          documents: ['Retention_Plan.pdf', 'Final_Records.pdf']
        }
      ],
      milestones: [
        { title: 'Treatment Start', date: '2023-09-15', completed: true },
        { title: 'Active Treatment', date: '2023-09-29', completed: true },
        { title: 'Treatment Complete', date: '2024-01-10', completed: true },
        { title: 'Retention Phase', date: '2024-01-10', completed: true }
      ]
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-100 text-green-800',
      current: 'bg-blue-100 text-blue-800',
      upcoming: 'bg-gray-100 text-gray-800',
      in_progress: 'bg-blue-100 text-blue-800'
    };
    return colors[status] || colors.upcoming;
  };

  const getStatusIcon = (status) => {
    const icons = {
      completed: FiCheckCircle,
      current: FiPlayCircle,
      upcoming: FiCalendar,
      in_progress: FiPlayCircle
    };
    return icons[status] || FiCalendar;
  };

  const filteredJourneys = patientJourneys.filter(journey =>
    journey.patientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    journey.treatmentType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Patient Journey Tracking</h1>
              <p className="text-gray-600 mt-1">Monitor and manage patient treatment progress in real-time.</p>
            </div>
            <div className="mt-4 sm:mt-0 flex items-center space-x-3">
              <div className="relative">
                <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search patients..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-64"
                />
              </div>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2">
                <SafeIcon icon={FiUser} className="w-4 h-4" />
                <span>New Journey</span>
              </button>
            </div>
          </div>
        </motion.div>

        {/* Stats Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8"
        >
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-blue-50 rounded-lg">
                <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">2</p>
                <p className="text-sm text-gray-600">Active Journeys</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-green-50 rounded-lg">
                <SafeIcon icon={FiCheckCircle} className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">1</p>
                <p className="text-sm text-gray-600">Completed</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-purple-50 rounded-lg">
                <SafeIcon icon={FiClock} className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">4.5</p>
                <p className="text-sm text-gray-600">Avg. Months</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="p-3 bg-orange-50 rounded-lg">
                <SafeIcon icon={FiActivity} className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">92%</p>
                <p className="text-sm text-gray-600">Satisfaction</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Filter Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6"
        >
          <div className="flex space-x-8">
            {[
              { id: 'current', label: 'Current Journeys', count: patientJourneys.filter(j => j.status === 'in_progress').length },
              { id: 'completed', label: 'Completed', count: patientJourneys.filter(j => j.status === 'completed').length },
              { id: 'all', label: 'All Journeys', count: patientJourneys.length }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveJourney(tab.id)}
                className={`
                  py-2 px-1 border-b-2 font-medium text-sm transition-colors
                  ${activeJourney === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                  }
                `}
              >
                {tab.label}
                <span className="ml-2 px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Patient Journeys */}
        <div className="space-y-6">
          {filteredJourneys.map((journey, index) => (
            <motion.div
              key={journey.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden"
            >
              {/* Journey Header */}
              <div className="p-6 border-b border-gray-100">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{journey.patientName}</h3>
                    <p className="text-gray-600">ID: {journey.patientId} • {journey.treatmentType}</p>
                  </div>
                  <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(journey.status)}`}>
                    {journey.status === 'completed' ? 'Completed' : 'In Progress'}
                  </span>
                </div>

                {/* Progress Overview */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Progress</p>
                    <div className="flex items-center space-x-2">
                      <div className="flex-1 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-blue-500 to-cyan-500 h-2 rounded-full transition-all"
                          style={{ width: `${journey.progress}%` }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{journey.progress}%</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Timeline</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(journey.startDate).toLocaleDateString()} - {
                        journey.completedDate 
                          ? new Date(journey.completedDate).toLocaleDateString()
                          : new Date(journey.estimatedCompletion).toLocaleDateString()
                      }
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Treatment Team</p>
                    <p className="text-sm font-medium text-gray-900">
                      {journey.primaryProvider} • {journey.specialist}
                    </p>
                  </div>
                </div>

                {/* Milestones */}
                <div className="flex items-center space-x-4">
                  {journey.milestones.map((milestone, i) => (
                    <div key={i} className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${
                        milestone.completed ? 'bg-green-500' : 'bg-gray-300'
                      }`}></div>
                      <div>
                        <p className={`text-xs font-medium ${
                          milestone.completed ? 'text-gray-900' : 'text-gray-500'
                        }`}>{milestone.title}</p>
                        <p className="text-xs text-gray-500">{new Date(milestone.date).toLocaleDateString()}</p>
                      </div>
                      {i < journey.milestones.length - 1 && (
                        <div className={`w-8 h-0.5 ${
                          milestone.completed ? 'bg-green-500' : 'bg-gray-300'
                        }`}></div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Treatment Steps */}
              <div className="p-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Treatment Steps</h4>
                <div className="space-y-4">
                  {journey.steps.map((step, stepIndex) => (
                    <motion.div
                      key={step.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.4 + stepIndex * 0.05 }}
                      className="relative flex items-start space-x-4 p-4 rounded-lg border border-gray-200 hover:border-blue-200 transition-colors"
                    >
                      {/* Timeline Line */}
                      {stepIndex < journey.steps.length - 1 && (
                        <div className="absolute left-6 top-12 w-0.5 h-16 bg-gray-200"></div>
                      )}

                      {/* Status Icon */}
                      <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${getStatusColor(step.status)}`}>
                        <SafeIcon icon={getStatusIcon(step.status)} className="w-5 h-5" />
                      </div>

                      {/* Step Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h5 className="font-medium text-gray-900">{step.title}</h5>
                            <div className="flex items-center space-x-3 mt-1 text-sm text-gray-600">
                              <div className="flex items-center space-x-1">
                                <SafeIcon icon={FiUser} className="w-3 h-3" />
                                <span>{step.provider}</span>
                              </div>
                              <div className="flex items-center space-x-1">
                                <SafeIcon icon={FiClock} className="w-3 h-3" />
                                <span>{step.duration}</span>
                              </div>
                            </div>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(step.status)}`}>
                            {step.status.replace('_', ' ')}
                          </span>
                        </div>

                        <p className="text-sm text-gray-600 mb-3">{step.notes}</p>

                        {step.completedDate && (
                          <p className="text-xs text-gray-500 mb-3">
                            Completed: {new Date(step.completedDate).toLocaleDateString()}
                          </p>
                        )}

                        {step.scheduledDate && !step.completedDate && (
                          <p className="text-xs text-blue-600 mb-3">
                            Scheduled: {new Date(step.scheduledDate).toLocaleDateString()}
                          </p>
                        )}

                        {/* Documents */}
                        {step.documents.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-3">
                            {step.documents.map((doc, docIndex) => (
                              <button
                                key={docIndex}
                                className="flex items-center space-x-1 px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-full hover:bg-gray-200 transition-colors"
                              >
                                <SafeIcon icon={FiFileText} className="w-3 h-3" />
                                <span>{doc}</span>
                                <SafeIcon icon={FiDownload} className="w-3 h-3" />
                              </button>
                            ))}
                          </div>
                        )}

                        {/* Action Buttons for Current/Upcoming Steps */}
                        {(step.status === 'current' || step.status === 'upcoming') && (
                          <div className="flex items-center space-x-2">
                            <button className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-1">
                              <SafeIcon icon={FiCalendar} className="w-3 h-3" />
                              <span>Schedule</span>
                            </button>
                            <button className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-1">
                              <SafeIcon icon={FiMessageSquare} className="w-3 h-3" />
                              <span>Message</span>
                            </button>
                            <button className="px-3 py-1 bg-gray-100 text-gray-600 text-xs rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-1">
                              <SafeIcon icon={FiPhone} className="w-3 h-3" />
                              <span>Call</span>
                            </button>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PatientJourney;